import React, { useEffect, useState } from 'react';
import useWizard from '../Hooks/useWizard';
import useWizardForm from '../Hooks/useWizardForm';
import MagicForm from '../../../Components/Form/MagicForm';
import Loading from '../../../Components/Loading';
import { WizardAction, WizardRootItem } from '../Interfaces/Wizard.interface';
import useJob from '../../Base/Hooks/useJob';
import styles from './Wizard.module.scss';

export interface WizardJobWaitingSetup {
	waitingMessage?: string;
	failedMessage?: string;
	timeout?: number;
}

interface Props {
	wizardKey: string;
	initialData?: object;
	keepInitialData?: boolean;
	jobWaitingSetup?: WizardJobWaitingSetup;
	onWizardDone: (action?: WizardAction) => void;
	onActiveStepChange?: (step: WizardRootItem | null) => void;
}

const Wizard: React.FC<Props> = ({
	wizardKey,
	initialData,
	keepInitialData,
	jobWaitingSetup,
	onWizardDone,
	onActiveStepChange,
}) => {
	const [jobWaitingAction, setJobWaitingAction] = useState<WizardAction>();
	const { RenderJobWaiting, handleStartWaitingForJob } = useJob();

	const handleWizardDone = (action?: WizardAction) => {
		if (action?.jobId) {
			setJobWaitingAction(action);
			handleStartWaitingForJob(action.jobId);
			return;
		}
		onWizardDone(action);
	};

	const { activeStep, submitStep } = useWizard({
		key: wizardKey,
		initialData,
		onWizardDone: handleWizardDone,
	});
	const { formScheme, defaultValues } = useWizardForm(activeStep);

	useEffect(() => {
		onActiveStepChange?.(activeStep);
	}, [activeStep]);

	const handleSubmit = async (values: any) => {
		await submitStep({
			...values,
			...(keepInitialData ? initialData : {}),
		});
	};

	const jobWaiting = (
		<RenderJobWaiting
			onJobSuccess={() => onWizardDone(jobWaitingAction)}
			onJobFailed={() => onWizardDone(jobWaitingAction)}
			onJobSkipWaiting={() => onWizardDone(jobWaitingAction)}
			{...jobWaitingSetup}
		/>
	);

	if (!formScheme) {
		return (
			<>
				<Loading />
				{jobWaiting}
			</>
		);
	}

	return (
		<div className={styles.wrapper}>
			<MagicForm
				formScheme={formScheme}
				defaultValues={defaultValues}
				onSubmit={handleSubmit}
				hideSubmitButton
			/>
			{jobWaiting}
		</div>
	);
};

export default Wizard;
